import { AxiosGlobal } from "../shared/axios";
import { AxiosPromise } from "axios";
import configs from "../../configs";
import { store } from "../../Components/store/store";

// Improved timezone handling utilities
const getTimezoneInfo = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset = new Date().getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offset / 60));
    const offsetMinutes = Math.abs(offset % 60);
    const offsetStr =
        (offset <= 0 ? '+' : '-') +
        (offsetHours < 10 ? '0' : '') +
        offsetHours +
        ':' +
        (offsetMinutes < 10 ? '0' : '') +
        offsetMinutes;

    return {
        timezone,
        offset: offsetStr,
        fullTimezone: `${timezone} (UTC${offsetStr})`
    };
};

// Utility to format date for API (always include time)
const formatDateForAPI = (dateInput: string | Date): string => {
    let date: Date;

    if (typeof dateInput === 'string') {
        // If it's just a date (YYYY-MM-DD), add noon time
        if (dateInput.length === 10) {
            date = new Date(dateInput + 'T12:00:00');
        } else {
            date = new Date(dateInput);
        }
    } else {
        date = dateInput;
    }

    // Format as ISO string but remove the 'Z' to let backend handle timezone conversion
    return date.toISOString().slice(0, 19);
};

export class DashboardLiveApi extends AxiosGlobal {

    getMedia(page: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/my-media?page=${page}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState()?.data?.login?.value?.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getLegacyMedia(page: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/legacy-media?page=${page}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState()?.data?.login?.value?.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getUserData(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/user-profile`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getUserSubscription(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/subscription`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    markSubscriptionModalViewed(): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/dashboard/mark-subscription-modal-viewed`, {}, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getSubscriptionModalStatus(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/dashboard/subscription-modal-status`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    toggleAutoRenew(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/subscription/toggle-auto-renew`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    cancelSubscription(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/subscription/cancel`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    updatePaymentMethod(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/subscription/update-payment-method`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    updateSubscriptionPlan(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/subscription/update-plan`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getBillingHistory(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/transactions`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    downloadInvoice(invoiceId: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/transactions/${invoiceId}/receipt`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getMediaUsage(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/media-usage`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    deleteMedia(id: any): AxiosPromise<Array<any>> {
        return this.axios.delete(`${configs.context}/delete-media/${id}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    // IMPROVED SCHEDULED MEDIA METHODS

    getScheduledMedia(): AxiosPromise<Array<any>> {
        const { timezone, fullTimezone } = getTimezoneInfo();
        const timestamp = Date.now();

        console.log(`Fetching scheduled media with timezone: ${fullTimezone}`);

        return this.axios.get(`${configs.context}/schedule-media?t=${timestamp}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*",
                "X-Timezone": timezone,
                "Cache-Control": "no-cache, no-store, must-revalidate",
                "Pragma": "no-cache"
            },
        });
    }

    getLatestScheduledMedia(page: number = 1): AxiosPromise<Array<any>> {
        const { timezone, fullTimezone } = getTimezoneInfo();
        const timestamp = Date.now();

        console.log(`Fetching latest scheduled media page ${page} with timezone: ${fullTimezone}`);

        return this.axios.get(`${configs.context}/schedule-media/latest?page=${page}&t=${timestamp}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*",
                "X-Timezone": timezone,
                "Cache-Control": "no-cache, no-store, must-revalidate",
                "Pragma": "no-cache"
            },
        });
    }

    getScheduledMediaByDate(date: string): AxiosPromise<Array<any>> {
        const { timezone, fullTimezone } = getTimezoneInfo();
        const timestamp = Date.now();

        console.log(`Fetching scheduled media for date ${date} with timezone: ${fullTimezone}`);

        return this.axios.get(`${configs.context}/schedule-media/date/${date}?t=${timestamp}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*",
                "X-Timezone": timezone,
                "Cache-Control": "no-cache, no-store, must-revalidate",
                "Pragma": "no-cache"
            },
        });
    }

    scheduleMedia(data: any): AxiosPromise<Array<any>> {
        const { timezone, fullTimezone } = getTimezoneInfo();

        // LOG: Check what data is being sent
        console.log('Original data being sent to API:', data);

        // IMPORTANT: Do NOT modify the delivery_date here
        // The delivery_date should already be in YYYY-MM-DD format from the frontend

        console.log(`Scheduling media for date ${data.delivery_date} with timezone: ${fullTimezone}`);
        console.log('Final data being sent to backend:', data);

        return this.axios.post(`${configs.context}/schedule-media`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*",
                "X-Timezone": timezone
            },
        });
    }

    deleteScheduledMedia(id: number): AxiosPromise<Array<any>> {
        return this.axios.delete(`${configs.context}/schedule-media/${id}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    // RECEIVED MEDIA METHODS

    getReceivedMedia(page: number = 1, type: string = 'all', perPage: number = 10): AxiosPromise<Array<any>> {
        const { timezone } = getTimezoneInfo();

        return this.axios.get(`${configs.context}/received-media?page=${page}&type=${type}&per_page=${perPage}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*",
                "X-Timezone": timezone
            },
        });
    }

    getMediaDetails(id: number, type: string): AxiosPromise<Array<any>> {
        const { timezone } = getTimezoneInfo();

        return this.axios.get(`${configs.context}/received-media/${id}/${type}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*",
                "X-Timezone": timezone
            },
        });
    }

    markMediaAsViewed(id: number, type: string): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/received-media/${id}/${type}/view`, {}, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    // RECIPIENT METHODS

    // RECIPIENT METHODS

    checkContacts(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/checkContacts`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getRecipient(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/recipients`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getRecipientGroup(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/recipient-groups`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getRecipientDetails(id: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/recipient-details/${id}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    // SHARED MEDIA APIS
    getSharedUsers(id: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/share/media/${id}/users`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getSharedWithMe(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/shared-with-me`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    shareMedia(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/share/media`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    removeSharedAccess(data: any): AxiosPromise<Array<any>> {
        return this.axios.delete(`${configs.context}/share/media/${data}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    // SHARED MEDIA GROUP APIS
    createGroup(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/groups/create`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    myGroups(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/groups/my-groups`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getMySharedGroups(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/groups/shared-with-me`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    updateGroup(id: any, data: any): AxiosPromise<Array<any>> {
        return this.axios.put(`${configs.context}/groups/${id}`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    deleteGroup(data: any): AxiosPromise<Array<any>> {
        return this.axios.delete(`${configs.context}/groups/${data}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    updateUserData(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/update-profile`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    processPayment(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/pay`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    deleteRecipient(data: any): AxiosPromise<Array<any>> {
        return this.axios.delete(`${configs.context}/recipients/${data}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    createRecipient(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/create-recipient`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    editRecipient(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/update-recipient`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    UploadMedia(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/upload-media`, data, {
            headers: {
                "Content-Type": "multipart/form-data",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    uploadBankDoc(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/dashboard/upload-bank-docs`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    uploadKyc(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/dashboard/kyc-verify`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    applyStudentLoan(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/dashboard/apply-student-loan`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    createDynamicAccount(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/payment/providus/create-dynamic-account`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    makeSchoolPayment(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/payment/make-school-payment`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    addSchool(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/dashboard/add-school`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getAllSchool(pageNo: any, query: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/dashboard/get-all-schools?page=${pageNo}&search=${query?.search}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getSchoolList(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/dashboard/get-school-list`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getPaymentHistory(pageNo: any, query: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/payment/payment-history?page=${pageNo}&search=${query?.search}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getPendingPaymentHistory(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/payment/pending-payment-history`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getPaymentDetail(id: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/payment/get-payment-details/${id}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    verifyLoan(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/dashboard/verify-loan`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    verifyKyc(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/dashboard/verify-kyc`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    makePayment(amount: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/payment/make-payment`, amount, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    makePaymentFromWallet(amount: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/payment/make-school-payment-from-wallet`, amount, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    makePaymentWithInterswitch(amount: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/payment/make-payment-with-interswitch`, amount, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    generateQuote(data: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/payment/flutterwave/get-exchange-rate?amount=${data?.amount}&from=${data?.from}&to=${data?.to}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    swap(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/payment/monieverse/swap`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getCallback(reference: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/payment/callback?reference=${reference}`, {
            headers: {
                "Content-Type": "application/json",
                'mode': 'no-cors',
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getDashboardData(): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/dashboard/get-dashboard-data`, {
            headers: {
                "Content-Type": "application/json",
                'mode': 'no-cors',
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    verifyInterswitchPayment(reference: any, amount: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/payment/verify-payment-with-interswitch?reference=${reference}&amount=${amount}`, {
            headers: {
                "Content-Type": "application/json",
                'mode': 'no-cors',
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    createCreditEligibility(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/credit-eligibility/create-credit-eligibility`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getSingleCreditEligibity(id: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/credit-eligibility/get-single-credit-form/${id}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    createSchoolForm(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/credit-eligibility/create-school-form`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    applyStudyLoan(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/dashboard/apply-study-loan`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    admissionProcess(data: any): AxiosPromise<Array<any>> {
        return this.axios.post(`${configs.context}/dashboard/admission-process`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    getSingleSchoolForm(id: any): AxiosPromise<Array<any>> {
        return this.axios.get(`${configs.context}/credit-eligibility/get-single-school-form/${id}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }
}